// ../../react-shim.js
import React from "react";

// src/portal-manager.tsx
import { createContext } from "@nature-ui/react-utils";

// ../utils/dist/index.esm.js
import React2 from "react";
import React22 from "react";
var __DEV__ = process.env.NODE_ENV !== "production";
var _window;
try {
  _window = window;
} catch {
}
var getWindow = (node) => {
  var _a;
  return ((_a = node == null ? void 0 : node.ownerDocument) == null ? void 0 : _a.defaultView) ?? _window;
};
var checkIsBrowser = () => {
  const __window = getWindow();
  return Boolean(
    typeof __window !== "undefined" && __window.document && __window.document.createElement
  );
};
var isBrowser = checkIsBrowser();
var SELECTORS = [
  "input",
  "select",
  "textarea",
  "a[href]",
  "area[href]",
  "button",
  "[tabindex]",
  "audio[controls]",
  "video[controls]",
  "[contenteditable]:not([contenteditable=false])"
];
var selector = SELECTORS.join();
var minSafeInteger = Number.MIN_SAFE_INTEGER || -9007199254740991;
var maxSafeInteger = Number.MAX_SAFE_INTEGER || 9007199254740991;

// src/portal-manager.tsx
import { jsx } from "react/jsx-runtime";
var [PortalManagerContextProvider, usePortalManager] = createContext({
  strict: false,
  name: "PortalManagerContext"
});
var PortalManager = (props) => {
  const { children, zIndex } = props;
  return /* @__PURE__ */ jsx(PortalManagerContextProvider, {
    value: { zIndex },
    children
  });
};
if (__DEV__) {
  PortalManager.displayName = "PortalManager";
}

// src/portal.tsx
import { useForceUpdate, useSafeLayoutEffect } from "@nature-ui/hooks";
import { createContext as createContext2 } from "@nature-ui/react-utils";
import React3 from "react";
import { createPortal } from "react-dom";
import { jsx as jsx2 } from "react/jsx-runtime";
var [PortalContextProvider, usePortalContext] = createContext2({
  strict: false,
  name: "PortalContext"
});
var PORTAL_CLASSNAME = "nature-portal";
var PORTAL_SELECTOR = `.${PORTAL_CLASSNAME}`;
var Container = (props) => /* @__PURE__ */ jsx2("div", {
  className: "nature-portal-zIndex",
  style: {
    zIndex: props.zIndex,
    position: "absolute",
    top: 0,
    left: 0,
    right: 0
  },
  children: props.children
});
var DefaultPortal = (props) => {
  const { appendToParentPortal, children } = props;
  const tempNode = React3.useRef(null);
  const portal = React3.useRef(null);
  const forceUpdate = useForceUpdate();
  const parentPortal = usePortalContext();
  const manager = usePortalManager();
  useSafeLayoutEffect(() => {
    if (!tempNode.current)
      return;
    const doc = tempNode.current.ownerDocument;
    const host = appendToParentPortal ? parentPortal ?? doc.body : doc.body;
    if (!host)
      return;
    portal.current = doc.createElement("div");
    portal.current.className = PORTAL_CLASSNAME;
    host.appendChild(portal.current);
    forceUpdate();
    const portalNode = portal.current;
    return () => {
      if (host.contains(portalNode)) {
        host.removeChild(portalNode);
      }
    };
  }, []);
  const _children = (manager == null ? void 0 : manager.zIndex) ? /* @__PURE__ */ jsx2(Container, {
    zIndex: manager.zIndex,
    children
  }) : children;
  return portal.current ? createPortal(
    /* @__PURE__ */ jsx2(PortalContextProvider, {
      value: portal.current,
      children: _children
    }),
    portal.current
  ) : /* @__PURE__ */ jsx2("span", {
    ref: tempNode
  });
};
var ContainerPortal = (props) => {
  const { children, containerRef, appendToParentPortal } = props;
  const containerEl = containerRef.current;
  const host = containerEl ?? (isBrowser ? document.body : void 0);
  const portal = React3.useMemo(() => {
    const node = containerEl == null ? void 0 : containerEl.ownerDocument.createElement("div");
    if (node)
      node.className = PORTAL_CLASSNAME;
    return node;
  }, [containerEl]);
  const forceUpdate = useForceUpdate();
  useSafeLayoutEffect(() => {
    forceUpdate();
  }, []);
  useSafeLayoutEffect(() => {
    if (!portal || !host)
      return;
    host.appendChild(portal);
    return () => {
      host.removeChild(portal);
    };
  }, [portal, host]);
  if (host && portal) {
    return createPortal(
      /* @__PURE__ */ jsx2(PortalContextProvider, {
        value: appendToParentPortal ? portal : null,
        children
      }),
      portal
    );
  }
  return null;
};
var Portal = (props) => {
  const { containerRef, ...rest } = props;
  return containerRef ? /* @__PURE__ */ jsx2(ContainerPortal, {
    containerRef,
    ...rest
  }) : /* @__PURE__ */ jsx2(DefaultPortal, {
    ...rest
  });
};
Portal.defaultProps = {
  appendToParentPortal: true
};
Portal.className = PORTAL_CLASSNAME;
Portal.selector = PORTAL_SELECTOR;
if (__DEV__) {
  Portal.displayName = "Portal";
}
export {
  Portal,
  PortalManager,
  usePortalManager
};
